import { useEffect, useState } from "react";
import { Loader2, Users, Clock, CheckCircle2, Ban, Activity, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";

const CARDS = [
  { key: "total_customers", label: "Total Customers", icon: Users, theme: "indigo", filter: "all" },
  { key: "pending", label: "Pending Approvals", icon: Clock, theme: "amber", filter: "pending" },
  { key: "approved", label: "Approved Customers", icon: CheckCircle2, theme: "emerald", filter: "approved" },
  { key: "blocked", label: "Blocked Customers", icon: Ban, theme: "rose", filter: "blocked" },
  { key: "active", label: "Active (30 days)", icon: Activity, theme: "violet", filter: "active" },
  { key: "total_orders", label: "Total Orders", icon: ShoppingBag, theme: "teal", filter: null, goOrders: true },
];

const THEMES = {
  indigo: { card: "from-indigo-50 to-white border-indigo-100 hover:border-indigo-300", icon: "bg-indigo-600 shadow-indigo-200", num: "text-indigo-900", link: "text-indigo-600", bar: "bg-indigo-500" },
  amber: { card: "from-amber-50 to-white border-amber-100 hover:border-amber-300", icon: "bg-amber-500 shadow-amber-200", num: "text-amber-900", link: "text-amber-700", bar: "bg-amber-500" },
  emerald: { card: "from-emerald-50 to-white border-emerald-100 hover:border-emerald-300", icon: "bg-emerald-600 shadow-emerald-200", num: "text-emerald-900", link: "text-emerald-700", bar: "bg-emerald-500" },
  rose: { card: "from-rose-50 to-white border-rose-100 hover:border-rose-300", icon: "bg-rose-500 shadow-rose-200", num: "text-rose-900", link: "text-rose-600", bar: "bg-rose-500" },
  violet: { card: "from-violet-50 to-white border-violet-100 hover:border-violet-300", icon: "bg-violet-600 shadow-violet-200", num: "text-violet-900", link: "text-violet-700", bar: "bg-violet-500" },
  teal: { card: "from-teal-50 to-white border-teal-100 hover:border-teal-300", icon: "bg-teal-600 shadow-teal-200", num: "text-teal-900", link: "text-teal-700", bar: "bg-teal-500" },
};

export default function AdminDashboard({ onGoFilter, onGoOrders }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
  }, []);

  if (!stats) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5">
        {CARDS.map((c) => {
          const Icon = c.icon;
          const t = THEMES[c.theme];
          const clickable = !!c.filter || !!c.goOrders;
          const Comp = clickable ? "button" : "div";
          const handleClick = c.goOrders ? () => onGoOrders?.() : () => onGoFilter?.(c.filter);
          return (
            <Comp
              key={c.key}
              type={clickable ? "button" : undefined}
              data-testid={`stat-${c.key}`}
              onClick={clickable ? handleClick : undefined}
              className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-4 text-left shadow-sm transition-[transform,box-shadow,border-color] duration-200 sm:p-5 ${t.card} ${
                clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-blue-200" : ""
              }`}
            >
              <span className={`absolute inset-x-0 top-0 h-1 ${t.bar}`} />
              <div className={`mb-3 grid h-11 w-11 place-items-center rounded-xl shadow-md ${t.icon}`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <p className={`text-3xl font-extrabold tracking-tight ${t.num}`} data-testid={`stat-value-${c.key}`}>{stats[c.key] ?? 0}</p>
              <p className="mt-1 text-xs font-semibold text-slate-600 sm:text-sm">{c.label}</p>
              {clickable && <p className={`mt-2 text-[11px] font-bold ${t.link}`}>View →</p>}
            </Comp>
          );
        })}
      </div>

      {stats.pending > 0 && (
        <button
          data-testid="dashboard-pending-cta"
          onClick={() => onGoFilter?.("pending")}
          className="mt-5 flex w-full items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm text-left transition hover:bg-amber-100"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <Clock className="h-4 w-4" /> {stats.pending} customer(s) waiting for approval
          </span>
          <span className="text-xs font-bold text-amber-700">Review →</span>
        </button>
      )}
    </div>
  );
}
